import { MessageBarType } from '@fluentui/react';
import { Button, MessageBar, TextField, useTheme } from '@h2oai/ui-kit';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CreateUserGroupsRequest } from '../../admin-center/gen/custom_group_management/custom_group_management_pb';
import { EnvIdRequest } from '../../admin-center/gen/environment_info/natgw_pb';
import { useAdminCenterService } from '../../admin-center/hooks';
import { useUser } from '../../utils/hooks';
import { buildUserContext } from './common/utils';
import {
  createButtonStyles,
  createGroupFormStyles,
  createGroupSectionStyles,
  messageBarContainerStyles,
  sectionHeaderStyles,
} from './components/CustomGroupManagement/CustomGroupManagement.styles';
import UserGroupsList from './components/CustomGroupManagement/GroupList/GroupList';
import PageWrapper from './PageWrapper';

export default function CustomGroupManagement() {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const [groupName, setGroupName] = useState('');
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);
  const [isEnvIdAvailable, setIsEnvIdAvailable] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const cachedEnvId = useRef<string | null>(null);
  const envIdPromise = useRef<Promise<string> | null>(null);
  const [envId, setEnvId] = useState(localStorage.getItem('envId') || null);

  const isFormValid = useCallback(() => {
    const groupNames: string[] = groupName.includes(',')
      ? groupName
          .split(',')
          .map((g) => g.trim())
          .filter((g) => g.length > 0)
      : [groupName.trim()];

    if (groupNames.length === 0 || groupNames.some((g) => g.length === 0)) {
      return false;
    }

    return !groupNames.some((group) => group.includes(' '));
  }, [groupName]);

  const getFieldErrorMessage = useCallback(() => {
    const groupNames: string[] = groupName.includes(',')
      ? groupName
          .split(',')
          .map((g) => g.trim())
          .filter((g) => g.length > 0)
      : [groupName.trim()];

    if (groupNames.length === 0) {
      return undefined;
    }

    if (groupNames.some((group) => group.includes(' '))) {
      return 'Group names cannot contain spaces';
    }

    return undefined;
  }, [groupName]);

  const handleCreateGroupChange = (
    _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setGroupName(newValue || '');
  };

  const handleCreateClick = useCallback(async () => {
    if (!isFormValid()) return;

    try {
      const userContext = await buildUserContext(user);
      const groupNames: string[] = groupName.includes(',')
        ? groupName
            .split(',')
            .map((g) => `h2o-${envId}-${g.trim()}`)
            .filter((g) => g.length > 0)
        : [`h2o-${envId}-${groupName.trim()}`];
      const request: CreateUserGroupsRequest = {
        groupNames: groupNames,
        userContext: userContext,
      };
      const response = await service.createUserGroupsRequest(request);
      if (response.statusUpdate) {
        const statusMessage = response.statusUpdate;
        setResponseMessage(statusMessage);
        setRefreshKey((prev) => prev + 1);
      } else {
        setResponseMessage('Error: Failed to update member group');
      }
      setGroupName('');
    } catch (error) {
      setResponseMessage(`Network error: ${error}`);
    } finally {
      setIsRequestLoading(false);
    }
  }, [groupName, isFormValid, isRequestLoading, user, envId]);

  const fetchEnvId = async () => {
    const cachedValue = localStorage.getItem('envId');
    if (cachedValue) {
      setEnvId(cachedValue);
      cachedEnvId.current = cachedValue;
      return;
    }

    if (!envIdPromise.current) {
      envIdPromise.current = (async () => {
        try {
          const endIdRequest: EnvIdRequest = {};
          const envIdResponse = await service.getEnvId(endIdRequest);
          const id = envIdResponse.envId || '';
          cachedEnvId.current = id;
          return id;
        } catch (error) {
          envIdPromise.current = null;
          throw error;
        }
      })();
    }

    try {
      const id = await envIdPromise.current;
      setEnvId(id);
      localStorage.setItem('envId', id);
    } catch (error) {
      setIsEnvIdAvailable(false);
    }
  };
  useEffect(() => {
    fetchEnvId();
  }, []);

  const getMessageBarType = (message: string | null): MessageBarType => {
    if (!message) return MessageBarType.info;

    const lowerMessage = message.toLowerCase();
    if (lowerMessage.startsWith('success')) return MessageBarType.success;
    if (lowerMessage.startsWith('error')) return MessageBarType.error;
    return MessageBarType.info;
  };

  const getMessageContent = (message: string | null): string => {
    if (!message) return '';
    const parts = message.split(':');
    return parts.length > 1 ? parts.slice(1).join(':') : message;
  };

  const handleCreateButtonClick = () => {
    setIsRequestLoading(true);
    handleCreateClick();
  };

  return (
    <PageWrapper>
      <div style={createGroupSectionStyles(theme)}>
        <div style={sectionHeaderStyles(theme)}>
          <h2 style={{ margin: 0, color: theme.palette?.gray750 }}>Create Group</h2>
          <p style={{ color: theme.palette?.gray500, margin: '8px 0 0 0', fontSize: '14px' }}>
            Add new user groups to your organization.
          </p>
        </div>

        <div style={createGroupFormStyles()}>
          <TextField
            label="Group Name"
            placeholder="Enter group name"
            value={groupName}
            onChange={handleCreateGroupChange}
            errorMessage={getFieldErrorMessage()}
            prefix={envId ? `h2o-${envId}-` : ''}
            styles={{
              root: {
                width: '100%',
                minHeight: '76px',
              },
              fieldGroup: { borderRadius: '4px' },
            }}
          />
          <Button
            styles={createButtonStyles}
            text={isRequestLoading ? 'Creating...' : 'Create Group'}
            onClick={handleCreateButtonClick}
            disabled={!isFormValid() || isRequestLoading || !isEnvIdAvailable}
          />
        </div>

        {responseMessage && (
          <div style={messageBarContainerStyles()}>
            <MessageBar
              timeout={6000}
              onDismiss={() => setResponseMessage(null)}
              messageBarType={getMessageBarType(responseMessage)}
            >
              {getMessageContent(responseMessage)}
            </MessageBar>
          </div>
        )}
      </div>
      <UserGroupsList refreshKey={refreshKey} onMessage={(msg) => setResponseMessage(msg)} />
    </PageWrapper>
  );
}
